import { ModeEnum } from "../components/enums/modeEnum";
// Safe: stringHelper imports only SaveModeEnum, so this cannot close an import cycle.
import { getExtension } from "../components/helpers/stringHelper";
import { IPager } from "../interfaces/IPager";
import { IGraphPager } from "../interfaces/IGraphPager";
import { IFolderPanelInfo } from "../interfaces/IFolderPanelInfo";
import { SharePointInputTypes } from "./sharepointConstants";
import { InputEnum } from "../components/enums/NormalInputTypeEnum";
import { ComponentTypes } from "./config";
import { IGraphSearchPager } from "../interfaces/IGraphSearchPager";
import { WelcomeFlag } from "../interfaces/IUserProfile";

export const datasourceType = {
  sqlServer: "SQLServer"
};

export const displayHeight: string = "calc(100vh - 152px)"; //subtract header and breadcrumb heigths

export const searchTypes = {
  driveItem: "microsoft.graph.driveItem"
};

export const searchFields = {
  graph: ["rank", "isDocument", "isContainer", "contentTypeID", "displayName", "description", "id", "createdDateTime", "lastModifiedBy", "lastModifiedDateTime", "name", "webUrl", "sharepointIds", "parentReference", "size", "ProgID"],
}

export const searchParams = {
  maxSize: 15
};

export const filesPaging = {
  numberOfResults: 30
};

export const sharePointInternalFileName = "FileLeafRef";
export const sharePointInternalTitleName = "Title";
export const sharePointInternalAuthorName = "Author";
export const sharePointInternalEditorName = "Editor";
export const rowLockIndicator = "{_autoLock}";

export const sharepointType = {
  taxonomyTerm: "SP.Taxonomy.TermCollection"
};

export const microsoftGraph = {
  url: {
    search: "https://graph.microsoft.com/beta/search/query"
  }
};

export const API = {
  url: {
    outlook: { oneDrive: "oneDrive/UploadOutlookFile", sharepoint: "Sharepoint/CreateSharepointOutlookFiles" },
    office: { oneDrive: "oneDrive/UploadOfficeFile", sharepoint: "Sharepoint/CreateSharepointOfficeFile" },
    fileSystem: { sharepoint: "Sharepoint/UploadFileSystemFilesToSharePoint" }
  }
};

const sharePointView = {
  fields: "<ViewFields><FieldRef Name=\"DocIcon\"/><FieldRef Name=\"FileLeafRef\"/><FieldRef Name=\"Created\"/><FieldRef Name=\"Modified\"/><FieldRef Name=\"Editor\"/><FieldRef Name=\"PrivacySensitive\"/><FieldRef Name=\"PrivacySensitiveData\"/></ViewFields>",
  rowLimit: "<RowLimit Paged=\"TRUE\">30</RowLimit>"
}

export const sharepoint = {
  contentTypeFiltering:
    "ReadOnlyField eq false and FromBaseType eq false and TypeAsString ne 'BusinessData' and Hidden eq false",
  defaultContentType: "Name eq 'Document'",
  defaultViewXml: `<View>${sharePointView.fields}${sharePointView.rowLimit}</View>`,
  moveCopyAllViewXml: "<View><ViewFields><FieldRef Name=\"FileLeafRef\"/></ViewFields><RowLimit Paged=\"TRUE\">1000</RowLimit></View>",
  getDefaultFilteredViewXml: (query: string): string => {
    return `<View>${sharePointView.fields}${query}${sharePointView.rowLimit}</View>`;
  }
};

export const columnSearchTypes = {
  webUrl: "weburl",
  name: "name",
  fileType: "fileType",
  driveid: "driveid",
  itemid: "itemid",
  serverRelativePath: "serverrelativepath",
  sizeText: "sizetext",
  sizeValue: "sizevalue",
  modifiedText: "modifiedtext",
  modifiedValue: "modifiedvalue",
  modifiedBy: "modifiedby",
  siteUrl: "siteurl",
  contentTypeId: "contenttypeid",
  uniqueId: "uniqueid",
  checkoutUser: "checkoutuser",
  canEdit: "canedit",
  canDelete: "candelete"
};

export const requirementSet = {
  mailbox: "Mailbox"
};

export const officeFileType = {
  Word: "Word",
  Excel: "Excel",
  Powerpoint: "Powerpoint",
  Visio: "Visio",
  Access: "Access",
  Project: "Project",
  Publisher: "Publisher",
  Infopath: "Infopath",
  OneNote: "OneNote",
  Browser: "Browser",
  App: "App"
};

export const filters = {
  onlyFolders: "folder ne null"
};

export const initialFileOptionPanelInfoSetting = {
  driveId: "",
  isOpen: false,
  createdDateTime: null,
  lastModifiedDateTime: null,
  name: null,
  fileType: null,
  filePath: "",
  size: 0
};

export const initialFolderOptionPanelInfoSetting: IFolderPanelInfo = {
  isOpen: false,
  folder: {
    driveId: null,
    id: null,
    name: null
  }
};

export const initialPaging: IPager = {
  offset: 0,
  pageNumber: 0,
  top: filesPaging.numberOfResults,
  hasMoreResults: false,
  nextPageUrl: ''
};

export const initialGraphPaging: IGraphPager = {
  top: filesPaging.numberOfResults,
  hasMoreResults: false,
  nextLink: null
};

export const initialGraphSearchPager: IGraphSearchPager = {
  moreResultsAvailable: false,
  from: 0
};

const siteFilters = {
  defaultExclude: "-WebTemplate:POLICYCTR -WebTemplate:POINTPUBLISHINGHUB -WebTemplate:POINTPUBLISHINGPERSONAL -WebTemplate:POINTPUBLISHINGTOPIC -WebTemplate:APP -WebTemplate:APPCATALOG -WebTemplate:SPSPERS -WebTemplate:TEAMCHANNEL -WebTemplate:PWA -WebTemplate:EDISC",
  allLevels: "(contentclass:STS_Site OR contentclass:STS_Web)",
  topLevelOnly: "contentclass:STS_Site",
  subsLevelOnly: "contentclass:STS_Web"
}

export const siteQueries = {
  all: `${siteFilters.allLevels} ${siteFilters.defaultExclude}`,
  top: `${siteFilters.topLevelOnly} ${siteFilters.defaultExclude}`,
  subs: `${siteFilters.subsLevelOnly} ${siteFilters.defaultExclude}`,
  hubs: "IsHubSite:true"
}

export const storageKeys = {
  recentOutlookReadMode: "RecentOutlookReadMode",
  recentOutlookComposeMode: "RecentOutlookComposeMode",
  recentOfficeReadMode: "RecentOfficeReadMode",
  recentOfficeComposeMode: "RecentOfficeComposeMode",
  pinItOutlookFolder: "PinItOutlookFolder",
  pinItOutlookFile: "PinItOutlookFile",
  pinItOfficeFolder: "PinItOfficeFolder",
  pinItOfficeFile: "PinItOfficeFile"
};

export const shared = {
  params: {
    app: "app",
    tenantId: "tid",
    accountId: "acc",
    username: "username",
    resource: "res",
    language: "l",
    mailboxAddress: "ma",
    driveId: "did",
    itemId: "iid",
    embedded: "embed",
  }
};

export const advancedSearch = {
  params: {
    siteId: "siteId",
    documentLibraryId: "documentLibraryId",
    tenantId: "tenantId",
    mode: "mode",
    loadUserRecentItems: "ri",
    hasTeamsAccess: "tmsacc",
    hasSharePointAccess: "spacc",
    isTeams: "t",
    allowOpenInSharePoint: "aois",
    hubId: "hubId",
    teamGroupId: "tgid",
    teamDriveId: "tdid",
    // The site's role in its client groups, from the task pane's session copy
    // (encodeClientGroupRole); absent when the task pane did not know it.
    clientGroupRole: "cgr",
  }
};

export const contentManager = {
  // NOTE: contentManagerApp treats every querystring param NOT registered in
  // shared.params or contentManager.params as a pre-select filter candidate
  // (extractPreselectFilterPairs). Any new system param MUST be added to one of
  // those two objects, or it will be captured as a filter field name.
  params: {
    preloadLocation: "pl",
    preloadURL: "plu",
    disableLandingPage: "dlp",
    preselectFilters: "pf"
  }
};

export const pdfEmbed = {
  params: {
    isInOneDrive: "io"
  }
};

export const validSaveAsPdfExtentions: string[] = ["eml", "docx", "doc", "docm", "dot", "dotm", "dotx", "odt", "xlsx", "xls", "xlsb", "xlsm", "xlt", "xltm", "xltx", "csv", "pptm", "pptx", "potx", "potm", "htm", "html", "tiff", "tif", "rtf", "xps"];

/**
 * What "Scan for custom fields" can actually read. Mirrors the server's
 * `CustomFieldsColumn.IsScannableExtension`, which is `DocIOHelper.WordExtensions ∪
 * ExcelExtensions ∪ PowerPointExtensions` — the server stays the authority; this copy exists
 * only so the command can be hidden before a user asks for work that cannot happen.
 */
export const scanCustomFieldsExtensions: string[] = [
  "docx", "doc", "docm", "dot", "dotm", "dotx", "odt", "rtf",
  "xlsx", "xls", "xltx", "xlt", "xlsm", "xltm", "xlsb",
  "pptx",
];

/**
 * What "Suggest placeholders (AI)" can actually open. Mirrors
 * `TemplateAiSuggestOrchestrator`'s own Word/Excel/PowerPoint lists, which gate
 * `TemplateSession.Open`.
 *
 * DELIBERATELY NOT the same array as scanCustomFieldsExtensions, and not derived from it:
 * the AI orchestrator keeps its own lists and they differ by **.odt**, which scans fine but
 * has no AI path. Collapsing these two into one list would either hide the scan command on a
 * file it handles, or offer AI on a file whose first call is a refusal.
 */
export const suggestPlaceholdersExtensions: string[] = [
  "docx", "doc", "docm", "dot", "dotm", "dotx", "rtf",
  "xlsx", "xls", "xltx", "xlt", "xlsm", "xltm", "xlsb",
  "pptx",
];

/**
 * Case-insensitive extension test against one of the lists above.
 *
 * Uses the app's existing `getExtension` rather than splitting on "." here: that helper
 * already handles a name with no dot and a name ending in one, and a second copy of the same
 * parsing is the kind of thing that drifts from the original.
 */
export const isSupportedTemplateExtension = (fileName: string, supported: string[]): boolean =>
  supported.includes(getExtension(fileName ?? "").toLowerCase());

export enum FieldType {
  Invalid = 0,
  Integer = 1,
  Text = 2,
  Note = 3,
  DateTime = 4,
  Counter = 5,
  Choice = 6,
  Lookup = 7,
  Boolean = 8,
  Number = 9,
  Currency = 10,
  URL = 11,
  Computed = 12,
  Threading = 13,
  Guid = 14,
  MultiChoice = 15,
  GridChoice = 16,
  Calculated = 17,
  File = 18,
  Attachments = 19,
  User = 20,
  Recurrence = 21,
  CrossProjectLink = 22,
  ModStat = 23,
  Error = 24,
  ContentTypeId = 25,
  PageSeparator = 26,
  ThreadIndex = 27,
  WorkflowStatus = 28,
  AllDayEvent = 29,
  WorkflowEventType = 30,
  Geolocation = 31,
  OutcomeChoice = 32,
  Location = 33,
  Thumbnail = 34,
  MaxItems = 35
}

export const SharepointFilters = {
  metadata: {
    save: "$select=AllowMultipleValues,DefaultValue,Title,InternalName,Required,Id,SchemaXml,TypeAsString,ValidationFormula,ValidationMessage,MaximumValue,MinimumValue,ShowAsPercentage,AppendOnly,FillInChoice,Choices,CurrencyLocaleId,SelectionGroup,SelectionMode,AnchorId,IsTermSetValid,Open,SspId,TermSetId,EmId,EmFrom,Hidden,TextField&$filter=ReadOnlyField eq false and FromBaseType eq false and TypeAsString ne 'BusinessData' and Hidden eq false",
    saveSPList: "$select=AllowMultipleValues,DefaultValue,Title,InternalName,Required,Id,SchemaXml,TypeAsString,ValidationFormula,ValidationMessage,MaximumValue,MinimumValue,ShowAsPercentage,AppendOnly,FillInChoice,Choices,CurrencyLocaleId,SelectionGroup,SelectionMode,AnchorId,IsTermSetValid,Open,SspId,TermSetId,EmId,EmFrom,Hidden,TextField&$filter=ReadOnlyField eq false and Hidden eq false and Group ne '_Hidden'",
    search: `$select=AllowMultipleValues,DefaultValue,Title,InternalName,Required,Id,SchemaXml,TypeAsString,ValidationFormula,ValidationMessage,MaximumValue,MinimumValue,ShowAsPercentage,AppendOnly,FillInChoice,Choices,CurrencyLocaleId,SelectionGroup,SelectionMode,AnchorId,IsTermSetValid,Open,SspId,TermSetId,EmId,EmFrom,Hidden,TextField&$filter=(ReadOnlyField eq false or FieldTypeKind eq ${FieldType.DateTime} or FieldTypeKind eq ${FieldType.User} or FieldTypeKind eq ${FieldType.Calculated}) and FieldTypeKind ne ${FieldType.File} and FieldTypeKind ne ${FieldType.Attachments} and FieldTypeKind ne ${FieldType.Computed} and TypeAsString ne 'BusinessData' and Hidden eq false and Group ne '_Hidden'`
  }
};

export const invalidSaveModes: ModeEnum[] = [ModeEnum.mailCompose];
export const validSaveModes: ModeEnum[] = [ModeEnum.mailRead, ModeEnum.office];
export const validRefreshComponents = [ComponentTypes.SharePoint, ComponentTypes.Teams, ComponentTypes.OneDrive];

export const multipleSelectionTypes = [
  SharePointInputTypes.user,
  SharePointInputTypes.userMulti,
  SharePointInputTypes.taxonomyFieldType,
  SharePointInputTypes.taxonomyFieldTypeMulti,
  SharePointInputTypes.choice,
  SharePointInputTypes.multiChoice
];

export const dateIndexes = {
  from: 0,
  to: 1
};

export const internalSharepointName = {
  contentTypeId: "ContentTypeId"
};

export const validPeopleInputTypes = [InputEnum.peoplePickerSingle, InputEnum.peoplePickerMultiple];
export const validDocumentLibraryAdvancedSearchComponentTypes = [
  ComponentTypes.SharePointDocuments,
  ComponentTypes.SharePointFolders
];

export const teamDocumentLibrary = {
  defaultName: "Documents",
  defaultDirectory: "Shared Documents"
};

export const formatParts = {
  currency: "currency" as const
};

export const validPdfExtensions = [
  "csv",
  "doc",
  "docx",
  "odp",
  "ods",
  "odt",
  "pot",
  "potm",
  "potx",
  "pps",
  "ppsx",
  "ppsxm",
  "ppt",
  "pptm",
  "pptx",
  "rtf",
  "xls",
  "xlsx"
];

export const validPreviewExtension = ["3g2", "3gp", "3mf", "abap", "ada", "adp", "ahk", "ai", "arw", "as", "as3", "asc", "ascx", "asf", "asm", "asp", "awk", "bas", "bash", "bash_login", "bash_logout", "bash_profile", "bashrc", "bat", "bib", "bmp", "bsh", "build", "builder", "c", "c++", "capfile", "cbk", "cc", "cfc", "cfm", "cfml", "cl", "clj", "cmake", "cmd", "coffee", "cool", "cpp", "cpt", "cpy", "cr2", "crw", "cs", "cshtml", "cson", "csproj", "css", "csv", "ctp", "cur", "cxx", "d", "dcm", "dcm30", "ddl", "di. dif", "dic", "dicm", "dicom", "diff", "disco", "dml", "dng", "doc", "docm", "docx", "dotm", "dotx", "dtd", "dtml", "dwg", "el", "emake", "eml", "epub", "erb", "erf", "erl", "f90", "f95", "fbx", "fs", "fsi", "fsscript", "fsx", "gemfile", "gemspec", "gif", "gitconfig", "glb", "gltf", "go", "groovy", "gvy", "h", "h++", "haml", "handlebars", "hbs", "hcp", "heic", "heif", "hevc", "hrl", "hs", "htc", "htm", "html", "html", "hxx", "ico", "icon", "idl", "iim", "inc", "inf", "ini", "inl", "ipp", "irbrc", "jade", "jav", "java", "jpe", "jpeg", "jpg", "js", "json", "jsp", "jsx", "key", "l", "less", "lhs", "lisp", "log", "log", "lst", "ltx", "lua", "m", "m2ts", "m4v", "make", "markdn", "markdown", "markdown", "md", "md", "mdown", "mef", "mkdn", "ml", "mli", "mll", "mly", "mm", "mov", "movie", "mp3", "mp4", "mp4v", "mrw", "msg", "mts", "mud", "nef", "nfo", "nrw", "obj", "odp", "ods", "odt", "opml", "orf", "osascript", "out", "p", "pages", "pano", "pas", "patch", "pdf", "pef", "php", "php2", "php3", "php4", "php5", "pict", "pl", "plist", "ply", "pm", "png", "pod", "pot", "potm", "potx", "pp", "pps", "ppsm", "ppsx", "ppt", "pptm", "pptx", "profile", "properties", "ps", "ps1", "psb", "psd", "pt", "py", "pyw", "r", "rake", "rb", "rbx", "rc", "re", "readme", "reg", "rest", "resw", "resx", "rhtml", "rjs", "rprofile", "rpy", "rss", "rst", "rtf", "rw2", "rxml", "s", "sass", "scala", "scm", "sconscript", "sconstruct", "script", "scss", "sgml", "sh", "shtml", "sketch", "sltx", "sml", "spm", "sql", "stl", "sty", "tcl", "tex", "text", "textile", "tif", "tiff", "tld", "tli", "tmpl", "tpl", "ts", "txt", "url", "vb", "vi", "vim", "vsd", "vsdx", "wmv", "wsdl", "xaml", "xbm", "xcf", "xd", "xhtml", "xls", "xlsb", "xlsx", "xml", "xoml", "xsd", "xsl", "xslt", "yaml", "yaws", "yml", "z", "zip", "zsh"]

export const extensions = {
  pdf: "pdf",
  url: "url"
};

export const EmptyGuid: string = "00000000-0000-0000-0000-000000000000";

export const SharePointFolderContentTypeIds = {
  item: "0x0100",
  document: "0x0101",
  folder: "0x0120",
  documentSet: "0x0120D5"
}

export const decimalSteps = {
  1: "0.1",
  2: "0.01",
  3: "0.001",
  4: "0.0001",
  5: "0.00001"
}

export const piiConfig = {
  privacySentitiveColumnName: "PrivacySensitive",
  privacySentitiveDataColumnName: "PrivacySensitiveData",
  privacySentitiveDataDefault: "Not Checked"
}

export const FileSortOptionKeys = {
  SortFields: {
    Relevance: "Relevance",
    Name: "FileLeafRef",
    TimeLastModified: "Modified",
    TimeCreated: "Created",
    Type: "DocIcon",
    Size: "File_x0020_Size"
  },
  SortOrder: {
    Ascending: "Asc",
    Descending: "Desc"
  }
}

export const CopyMoveErrors = {
  DuplicateFilenames: "Multiple files with the same name cannot be copied/moved to the same location.",
  // Raised when one or more selected rows carry no usable FileRef, so their source
  // URL cannot be built. The batch is refused outright rather than copying a subset.
  UnresolvableSourcePaths: "The location of one or more selected files could not be determined.",
  // A MOVE where one or more rows already live in the chosen destination folder.
  // CreateCopyJobs rejects that outright, so it is caught before the request and
  // reported as the (already localized) same-location message. Copy is unaffected:
  // NameConflictBehavior 2 keeps both.
  SameSourceAndDestination: "One or more of the selected items are already in the destination folder.",
  // Stand-in for a failure that produced no message of its own. Exists so the copy/move
  // state always carries a TRUTHY error: the error indicator is gated on truthiness, so
  // an undefined error made a failed operation vanish with no feedback at all.
  Unknown: "The copy or move could not be completed."
}

export const PopUpConstants = {
  POPUP_WIDTH: 483,
  /**
   * Default popup window height
   */
  POPUP_HEIGHT: 615,
  /**
   * Name of the popup window starts with
   */
  POPUP_NAME_PREFIX: "docubird",
};

export const OfficeXMLNamespaces = {
  properties: "http://schemas.microsoft.com/office/2006/metadata/properties",
  types: "http://schemas.microsoft.com/office/2006/metadata/contentType"
}

export const DocubirdCache = {
  currentFileUniqueId: "currentFileUniqueId",
  selectedExchangeItems: "selectedExchangeItems",
  selectedExchangeItemsPending: "selectedExchangeItemsPending",
  selectedExchangeMailboxTarget: "selectedExchangeMailboxTarget",
  selectedExchangeMailboxUnresolved: "selectedExchangeMailboxUnresolved",
  sharedMailboxContexts: "sharedMailboxContexts",
  spEmailPropertyMappings: "spEmailPropertyMappings",
  copyJobResults: "copyJobResults",
  signingEnabled: "signingEnabled",
  signalREnabled: "signalREnabled",
  officeFileUniqueId: "officeFileUniqueId",
  hooks: "hooks",
  users: "users",
  datasourceDocumentTemplates: "dsDocTemplates",
  isStrictSearch: "isStrictSearch"
}

export const officeExtentions = {
  word: {
    items: ['doc', 'docm', 'docx', 'dot', 'dotm', 'dotx', 'odt'],
    values: 'doc%3B%23docm%3B%23docx%3B%23dot%3B%23dotm%3B%23dotx%3B%23odt'
  },
  excel: {
    items: ['csv', 'ods', 'xla', 'xlam', 'xll', 'xls', 'xlsb', 'xlsm', 'xlsx', 'xlt', 'xltm', 'xltx'],
    values: 'csv%3B%23ods%3B%23xla%3B%23xlam%3B%23xll%3B%23xls%3B%23xlsb%3B%23xlsm%3B%23xlsx%3B%23xlt%3B%23xltm%3B%23xltx'
  },
  powerPoint: {
    items: ['odp', 'pot', 'potm', 'potx', 'ppa', 'ppam', 'pps', 'ppsm', 'ppsx', 'ppt', 'pptm', 'pptx'],
    values: 'odp%3B%23pot%3B%23potm%3B%23potx%3B%23ppa%3B%23ppam%3B%23pps%3B%23ppsm%3B%23ppsx%3B%23ppt%3B%23pptm%3B%23pptx'
  },
  visio: {
    items: ['vdw', 'vsd', 'vsdm', 'vsdx', 'vsl', 'vss', 'vssm', 'vssx', 'vst', 'vstm', 'vstx'],
    values: 'vdw%3B%23vsd%3B%23vsdm%3B%23vsdx%3B%23vsl%3B%23vss%3B%23vssm%3B%23vssx%3B%23vst%3B%23vstm%3B%23vstx'
  }
}

export const OneNoteProgId: string = "OneNote.Notebook";

export const validSigningRequestExtensions: string[] = ["DOC", "DOCM", "DOCX", "DOT", "DOTM", "DOTX", "HTM", "HTML", "MSG", "PDF", "RTF", "TXT", "WPD", "XHTML", "XPS", "BMP", "GIF", "HEIC", "JPG", "JPEG", "PNG", "TIF", "TIFF", "POT", "POTX", "PPS", "PPT", "PPTM", "PPTX", "CSV", "XLS", "XLSM", "XLSX"];

// The first-run welcome (WelcomeSplash), one entry per app. The app names match the manifests'
// DisplayName and are not translated; the links are the same for every language.
export type WelcomeApp = "dms" | "outlook" | "office";

export interface IWelcomeAppConfig {
  flag: WelcomeFlag;
  appName: string;
  tourUrl: string;
  setupUrl: string;
}

export const welcomeApps: Record<WelcomeApp, IWelcomeAppConfig> = {
  dms: {
    flag: "welcomePendingDms",
    appName: "Docubird DMS",
    tourUrl: "https://support.docubird.com/dms/tour",
    setupUrl: "https://support.docubird.com/dms/setup"
  },
  outlook: {
    flag: "welcomePendingOutlook",
    appName: "Docubird for Outlook",
    tourUrl: "https://support.docubird.com/outlook/tour",
    setupUrl: "https://support.docubird.com/outlook/setup"
  },
  office: {
    flag: "welcomePendingOffice",
    appName: "Docubird for Office",
    tourUrl: "https://support.docubird.com/office/tour",
    setupUrl: "https://support.docubird.com/office/setup"
  }
};

// The task pane's app: Outlook for a mail item, Office for Word, Excel and PowerPoint.
export const getTaskPaneApp = (mode: ModeEnum): WelcomeApp =>
  mode === ModeEnum.mailRead || mode === ModeEnum.mailCompose ? "outlook" : "office";